const NEW_RELIC_POLL_INTERVAL = 1000;

export interface CSPViolation {
  blockedURL: string;
  columnNumber: number;
  disposition: string;
  documentURL: string;
  effectiveDirective: string;
  lineNumber: number;
  referrer: string;
  sample: string;
  sourceFile: string;
  statusCode: number;
  timestamp: string;
  url: string;
  userAgent: string;
}

/**
 * CSPReporter is a class that reports CSP violations to New Relic.
 * It uses the ReportingObserver API to capture CSP violations and
 * the PageAction API to report them to New Relic.
 *
 * It also supports a fallback mechanism for browsers that do not support
 * the ReportingObserver API.
 */
export class CSPReporter {
  private checkInterval: ReturnType<typeof setInterval> | null = null;
  private newRelicPollInterval = 100;
  private newRelicPollTimeout = 10000;

  constructor() {
    if (CONFIG?.newRelic?.enabled) {
      this.setupEarlyMonitoring();
      this.initializeNewRelic();
    }
  }

  static isNewRelicReady() {
    return !!(
      window.newrelic &&
      window.newrelic.initializedAgents &&
      window.newrelic.addPageAction &&
      Object.keys(window.newrelic.initializedAgents).length
    );
  }

  flushQueue(): void {
    if (window.__CSP_QUEUE__ && CSPReporter.isNewRelicReady()) {
      window.__CSP_QUEUE__.forEach((report: Report) => {
        window.newrelic.addPageAction('CSPViolation', this.formatViolation(report));
      });
      delete window.__CSP_QUEUE__;
    }
  }

  formatViolation(report: Report): CSPViolation {
    return {
      blockedURL: report.body?.blockedURL || '',
      columnNumber: report.body?.columnNumber || 0,
      disposition: report.body?.disposition || '',
      documentURL: report.body?.documentURL || '',
      effectiveDirective: report.body?.effectiveDirective || '',
      lineNumber: report.body?.lineNumber || 0,
      referrer: report.body?.referrer || '',
      sample: report.body?.sample || '',
      sourceFile: report.body?.sourceFile || '',
      statusCode: report.body?.statusCode || 0,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
  }

  initializeNewRelic(): void {
    const checkForNewRelic = () => {
      if (this.newRelicPollTimeout <= 0) return;
      if (CSPReporter.isNewRelicReady()) {
        this.flushQueue();
      } else {
        setTimeout(checkForNewRelic, this.newRelicPollInterval);
        this.newRelicPollTimeout -= this.newRelicPollInterval;
      }
    };
    checkForNewRelic();
  }

  queueForNewRelic(report: Report): void {
    if (!window.__CSP_QUEUE__) window.__CSP_QUEUE__ = [];
    window.__CSP_QUEUE__.push(report);
    if (this.checkInterval === null) {
      this.checkInterval = setInterval(() => {
        if (CSPReporter.isNewRelicReady()) {
          this.flushQueue();
          if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
          }
        }
      }, NEW_RELIC_POLL_INTERVAL);
    }
  }

  reportToNewRelic(report: Report): void {
    if (CSPReporter.isNewRelicReady())
      window.newrelic.addPageAction('CSPViolation', this.formatViolation(report));
    else this.queueForNewRelic(report);
  }

  setupEarlyMonitoring(): void {
    if (window.__CSP_VIOLATIONS__) {
      window.__CSP_VIOLATIONS__.forEach((report: Report) => {
        this.reportToNewRelic(report);
      });
      delete window.__CSP_VIOLATIONS__;
    }
    this.setupReportingObserver();
  }

  setupLegacyListener(): void {
    document.addEventListener('securitypolicyviolation', (e: Event) => {
      const ev = e as SecurityPolicyViolationEvent;
      const body: ReportBody = {
        blockedURL: ev.blockedURI,
        columnNumber: ev.columnNumber,
        effectiveDirective: ev.effectiveDirective,
        lineNumber: ev.lineNumber,
        sourceFile: ev.sourceFile,
        violatedDirective: ev.violatedDirective,
      };
      const report: Report = {
        body,
        timestamp: Date.now(),
        type: 'csp-violation',
        url: window.location.href,
      };
      this.reportToNewRelic(report);
    });
  }

  setupReportingObserver(): void {
    if (typeof ReportingObserver !== 'undefined') {
      const observer = new ReportingObserver(
        (reports: Report[]) => {
          reports.forEach((report: Report) => {
            if (report.type === 'csp-violation') this.reportToNewRelic(report);
          });
        },
        { types: ['csp-violation'] },
      );
      observer.observe();
    } else {
      this.setupLegacyListener();
    }
  }
}

const reporter = new CSPReporter();

export default reporter;
